import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Input } from 'reactstrap';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { Move, SettingsIcon, Trash2 } from 'lucide-react';
import interact from 'interactjs';
import { useParams } from 'react-router-dom';
import {
  CapacityComponent,
  CapacityType,
  DashboardWidget,
  GetPathFlowsRequest,
  PathFlowComponentElement,
  PathFlows,
} from '@/types';
import { CapacityComponentSearch } from '../capacityComponents/CapacityComponentSearch';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import {
  useCreateDashboardWidgetMutation,
  useDeleteDashboardWidgetMutation,
  useGetDashboardQuery,
  useGetPathFlowsMutation,
  useReorderDashboardWidgetsMutation,
  useUpdateDashboardMutation,
  useUpdateDashboardWidgetMutation,
} from '../services/edgeApi';
import { CapacityComponentBulkHistory } from './CapacityComponentBulkHistory';
import {
  FLOW_DASHBOARD_FORMATTING_KEY,
  FLOW_DASHBOARD_HISTORY_VIEW_KEY,
  FlowDashboardDisplaySettings,
  FlowDashboardEntryDirection,
  FlowDashboardFormattingOverrides,
  FlowDashboardFormattingSettings,
  FlowDashboardHistoryViewOverrides,
  FlowDashboardHistoryViewSettings,
  getFlowDashboardConceptStorageKey,
  getFlowDashboardDisplaySettings,
  getFlowDashboardFormattingOverrides,
  getFlowDashboardFormattingSettings,
  getFlowDashboardHistoryViewOverrides,
  getFlowDashboardHistoryViewSettings,
  getDirectionOptions,
  toFlowDashboardFormattingConfig,
  toFlowDashboardHistoryViewConfig,
} from './flowDashboardStorage';

type FlowDashboardEntry = {
  id: string;
  componentId: number;
  capacityType: CapacityType;
  direction: FlowDashboardEntryDirection;
  elementDirection?: PathFlowComponentElement['element_direction'];
};

type WatchlistPanelProps = {
  widget: DashboardWidget;
  entries: FlowDashboardEntry[];
  isEditMode: boolean;
  onAddComponent: (widgetId: number) => void;
  onDeleteWatchlist: (widgetId: number) => void;
  onRenameWatchlist: (widgetId: number, name: string) => void;
  onRemoveEntry: (widgetId: number, entryId: string) => void;
  onReorderEntries: (
    widgetId: number,
    sourceEntryId: string,
    targetEntryId: string,
  ) => void;
  onUpdateEntryDirection: (
    widgetId: number,
    entryId: string,
    direction: FlowDashboardEntryDirection,
  ) => void;
  onUpdateDisplaySettings: (
    widgetId: number,
    settings: FlowDashboardDisplaySettings,
  ) => void;
  dashboardFormatting: FlowDashboardFormattingSettings;
  onUpdateFormattingOverrides: (
    widgetId: number,
    overrides: FlowDashboardFormattingOverrides | null,
  ) => void;
  dashboardHistoryView: FlowDashboardHistoryViewSettings;
  onUpdateHistoryViewOverrides: (
    widgetId: number,
    overrides: FlowDashboardHistoryViewOverrides | null,
  ) => void;
};

const buildComponentElements = (
  entries: FlowDashboardEntry[],
): PathFlowComponentElement[] =>
  entries.map((entry) => ({
    component_id: entry.componentId,
    direction: entry.direction,
    element_direction: entry.elementDirection,
  }));

const isDirection = (value: any): value is FlowDashboardEntryDirection =>
  value === 'RECEIPT' ||
  value === 'DELIVERY' ||
  value === 'FORWARDHAUL' ||
  value === 'BACKHAUL';

const isElementDirection = (
  value: any,
): value is PathFlowComponentElement['element_direction'] =>
  value === 'RECEIPT' ||
  value === 'DELIVERY' ||
  value === 'FORWARDHAUL' ||
  value === 'BACKHAUL' ||
  value === 'BIDIRECTIONAL' ||
  value === 'CALCULATED' ||
  value === 'UNKNOWN' ||
  value === null ||
  value === undefined;

const parseWidgetEntries = (widget: DashboardWidget): FlowDashboardEntry[] => {
  const config = widget.config as any;
  const entries = config?.entries;

  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry, index) => {
      const capacityType =
        entry.capacityType === 'POINT' || entry.capacityType === 'SEGMENT'
          ? entry.capacityType
          : 'POINT';
      const allowed = getDirectionOptions(capacityType);
      const fallbackDirection = allowed[0] || 'RECEIPT';
      const direction = isDirection(entry.direction)
        ? entry.direction
        : fallbackDirection;

      return {
        id:
          typeof entry.id === 'string' && entry.id.length > 0
            ? entry.id
            : `${widget.id}-${index}`,
        componentId: Number(entry.componentId),
        capacityType,
        direction,
        elementDirection: isElementDirection(entry.elementDirection)
          ? entry.elementDirection
          : null,
      };
    })
    .filter((entry) => Number.isFinite(entry.componentId));
};

type NumericDraftInputProps = {
  ariaLabel: string;
  disabled?: boolean;
  min: number;
  onCommit: (value: number) => void;
  value: number;
};

const NumericDraftInput: React.FC<NumericDraftInputProps> = ({
  ariaLabel,
  disabled = false,
  min,
  onCommit,
  value,
}) => {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const nextValue = Number(draft);
    if (Number.isFinite(nextValue) && nextValue >= min) {
      if (nextValue !== value) {
        onCommit(nextValue);
      }
      return;
    }
    setDraft(String(value));
  };

  return (
    <Input
      aria-label={ariaLabel}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={draft}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          setDraft(String(value));
          event.currentTarget.blur();
        }
      }}
      style={{ width: 110 }}
    />
  );
};

const getErrorMessage = (error: unknown): string => {
  if (!error) {
    return 'Unable to resolve flows for this watchlist.';
  }

  const maybeFetchError = error as FetchBaseQueryError;
  if (typeof maybeFetchError.status === 'number' || maybeFetchError.status) {
    if (
      maybeFetchError.data &&
      typeof maybeFetchError.data === 'object' &&
      'error' in maybeFetchError.data &&
      typeof maybeFetchError.data.error === 'string'
    ) {
      return maybeFetchError.data.error;
    }

    return JSON.stringify(maybeFetchError.data ?? maybeFetchError.status);
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Unable to resolve flows for this watchlist.';
};

const mergePathFlows = (
  existingData: PathFlows | null,
  nextData: PathFlows,
): PathFlows => {
  if (!existingData) return nextData;

  const mergeRow = (existingRow: any, nextRow: any) => ({
    ...existingRow,
    ...nextRow,
    flows: [...(existingRow.flows || []), ...(nextRow.flows || [])],
    notice_slice: {
      ...existingRow.notice_slice,
      ...nextRow.notice_slice,
      records_with_date_cycle: [
        ...(existingRow.notice_slice?.records_with_date_cycle || []),
        ...(nextRow.notice_slice?.records_with_date_cycle || []),
      ],
    },
  });
  const evidenceKey = (row: any) =>
    [row.source_kind, row.child_kind, row.label, JSON.stringify(row.key)].join(
      '|',
    );
  const existingGroups = new Map(
    existingData.groups.map((group) => [group.group_id, group]),
  );

  return {
    groups: nextData.groups.map((nextGroup) => {
      const existingGroup = existingGroups.get(nextGroup.group_id);
      if (!existingGroup) return nextGroup;

      const existingEvidence = new Map(
        existingGroup.evidence_rows.map((row) => [evidenceKey(row), row]),
      );
      return {
        ...existingGroup,
        ...nextGroup,
        parent_row: mergeRow(existingGroup.parent_row, nextGroup.parent_row),
        evidence_rows: nextGroup.evidence_rows.map((nextRow) =>
          mergeRow(existingEvidence.get(evidenceKey(nextRow)) || {}, nextRow),
        ),
      };
    }),
  };
};

const getHistoryRangeLimit = (startGasDate: string, endGasDate: string) => {
  const start = new Date(`${startGasDate}T00:00:00Z`);
  const end = new Date(`${endGasDate}T00:00:00Z`);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const limit = Math.round((end.getTime() - start.getTime()) / millisecondsPerDay);
  return limit >= 0 ? limit : null;
};

const getHistoryRangeStart = (endGasDate: string, limit: number) => {
  const end = new Date(`${endGasDate}T00:00:00Z`);
  if (Number.isNaN(end.getTime())) return '';

  end.setUTCDate(end.getUTCDate() - limit);
  return end.toISOString().slice(0, 10);
};

const WatchlistPanel: React.FC<WatchlistPanelProps> = ({
  widget,
  entries,
  isEditMode,
  onAddComponent,
  onDeleteWatchlist,
  onRenameWatchlist,
  onRemoveEntry,
  onReorderEntries,
  onUpdateEntryDirection,
  onUpdateDisplaySettings,
  dashboardFormatting,
  onUpdateFormattingOverrides,
  dashboardHistoryView,
  onUpdateHistoryViewOverrides,
}) => {
  const [getPathFlows, { isLoading, error }] = useGetPathFlowsMutation();
  const [showSettings, setShowSettings] = useState(false);
  const [draftTitle, setDraftTitle] = useState(widget.title || '');
  const [flowHistoryData, setFlowHistoryData] = useState<PathFlows | null>(
    null,
  );
  const [flowHistoryEndGasDate, setFlowHistoryEndGasDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [flowHistoryStartGasDate, setFlowHistoryStartGasDate] = useState('');
  const [flowHistoryLimit, setFlowHistoryLimit] = useState(7);
  const [isFlowHistoryDateRangeOpen, setIsFlowHistoryDateRangeOpen] =
    useState(false);
  const entriesContainerRef = useRef<HTMLDivElement | null>(null);
  const [draggingEntryId, setDraggingEntryId] = useState<string | null>(null);
  const [dragOverEntryId, setDragOverEntryId] = useState<string | null>(null);
  const [dragPreviewShift, setDragPreviewShift] = useState(0);
  const dragOverEntryIdRef = useRef<string | null>(null);
  const [resolvedMetadataById, setResolvedMetadataById] = useState<
    Record<number, { label: string; shortLabel: string; tspName: string }>
  >({});
  const displaySettings = getFlowDashboardDisplaySettings(widget.config);
  const formattingOverrides = getFlowDashboardFormattingOverrides(widget.config);
  const hasFormattingOverride = Object.keys(formattingOverrides).length > 0;
  const formattingSettings = {
    ...dashboardFormatting,
    ...formattingOverrides,
  };
  const historyViewOverrides = getFlowDashboardHistoryViewOverrides(widget.config);
  const hasHistoryViewOverride = Object.keys(historyViewOverrides).length > 0;
  const historyViewSettings = {
    ...dashboardHistoryView,
    ...historyViewOverrides,
  };
  const [historyScope, setHistoryScope] = useState(
    historyViewSettings.defaultScope,
  );
  const isHistoryMode = historyScope === 'history';

  const componentElements = useMemo(() => buildComponentElements(entries), [entries]);
  const componentMetadataById = useMemo(() => {
    const metadata = new Map<
      number,
      { label?: string; shortLabel?: string; tspName?: string }
    >();
    const groups = flowHistoryData?.groups;

    if (!Array.isArray(groups)) {
      return metadata;
    }

    groups.forEach((group) => {
      const componentId = Number(group?.component_id);
      if (!Number.isFinite(componentId)) {
        return;
      }

      const parentRow = group?.parent_row;
      const label =
        typeof parentRow?.label === 'string' && parentRow.label.trim().length > 0
          ? parentRow.label.trim()
          : typeof parentRow?.description === 'string' &&
              parentRow.description.trim().length > 0
            ? parentRow.description.trim()
          : undefined;
      const shortLabel =
        typeof parentRow?.short_label === 'string' &&
        parentRow.short_label.trim().length > 0
          ? parentRow.short_label.trim()
          : label;
      const tspName =
        typeof parentRow?.tsp_name === 'string' && parentRow.tsp_name.trim().length > 0
          ? parentRow.tsp_name.trim()
          : undefined;

      metadata.set(componentId, { label, shortLabel, tspName });
    });

    return metadata;
  }, [flowHistoryData]);

  useEffect(() => {
    if (componentMetadataById.size === 0) {
      return;
    }

    setResolvedMetadataById((previous) => {
      const next = { ...previous };
      let changed = false;

      componentMetadataById.forEach((metadata, componentId) => {
        if (!metadata.label || !metadata.shortLabel || !metadata.tspName) {
          return;
        }

        const existing = previous[componentId];
        if (
          existing &&
          existing.label === metadata.label &&
          existing.shortLabel === metadata.shortLabel &&
          existing.tspName === metadata.tspName
        ) {
          return;
        }

        next[componentId] = {
          label: metadata.label,
          shortLabel: metadata.shortLabel,
          tspName: metadata.tspName,
        };
        changed = true;
      });

      return changed ? next : previous;
    });
  }, [componentMetadataById]);

  useEffect(() => {
    setResolvedMetadataById({});
  }, [widget.id]);
  const hasError = Boolean(error);
  const requestKey = useMemo(
    () => JSON.stringify(componentElements),
    [componentElements],
  );

  const loadPathFlows = async (
    endGasDate: string,
    limit: number,
    append = false,
  ) => {
    const request: GetPathFlowsRequest = {
      component_elements: componentElements,
      filter_by_direction: true,
      include_empty: false,
      limit,
      offset: endGasDate,
    };

    try {
      const nextData = await getPathFlows(request).unwrap();
      setFlowHistoryData((previousData) =>
        append ? mergePathFlows(previousData, nextData) : nextData,
      );
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (componentElements.length === 0) {
      setFlowHistoryData(null);
      return;
    }

    setFlowHistoryData(null);
    void loadPathFlows(flowHistoryEndGasDate, flowHistoryLimit);
  }, [componentElements, getPathFlows, requestKey]);

  useEffect(() => {
    setHistoryScope(historyViewSettings.defaultScope);
  }, [historyViewSettings.defaultScope]);

  const loadOlderHistory = () => {
    if (!isHistoryMode || flowHistoryStartGasDate) return;

    const previousPageEnd = new Date(`${flowHistoryEndGasDate}T00:00:00Z`);
    previousPageEnd.setUTCDate(
      previousPageEnd.getUTCDate() - flowHistoryLimit - 1,
    );
    const pageLimit = 30;
    void loadPathFlows(
      previousPageEnd.toISOString().slice(0, 10),
      pageLimit,
      true,
    ).then((didLoad) => {
      if (didLoad) {
        setFlowHistoryLimit((previousLimit) => previousLimit + pageLimit + 1);
      }
    });
  };

  const errorText = getErrorMessage(error);

  useEffect(() => {
    setDraftTitle(widget.title || '');
  }, [widget.title]);

  useEffect(() => {
    if (!showSettings || !entriesContainerRef.current || entries.length < 2) {
      return;
    }

    const container = entriesContainerRef.current;
    const rows = Array.from(
      container.querySelectorAll<HTMLElement>('[data-flow-dashboard-entry-id]'),
    );

    const interactables = rows.map((row) =>
      interact(row).draggable({
        listeners: {
          start(event) {
            const target = event.target as HTMLElement;
            const sourceEntryId = target.dataset.flowDashboardEntryId || null;
            setDraggingEntryId(sourceEntryId);
            setDragOverEntryId(null);
            dragOverEntryIdRef.current = null;

            const rowHeight = target.getBoundingClientRect().height;
            const containerStyle = window.getComputedStyle(container);
            const rowGap = Number.parseFloat(containerStyle.rowGap || '0') || 0;
            setDragPreviewShift(rowHeight + rowGap);

            target.style.willChange = 'transform';
            target.style.zIndex = '5';
          },
          move(event) {
            const target = event.target as HTMLElement;
            const currentY = Number.parseFloat(target.dataset.dragY || '0') || 0;
            const nextY = currentY + event.dy;
            const sourceEntryId = target.dataset.flowDashboardEntryId;

            target.dataset.dragY = `${nextY}`;
            target.style.transform = `translateY(${nextY}px)`;

            if (!sourceEntryId) {
              return;
            }

            const sourceRect = target.getBoundingClientRect();
            const sourceCenterY = sourceRect.top + sourceRect.height / 2;
            const rowElements = Array.from(
              container.querySelectorAll<HTMLElement>('[data-flow-dashboard-entry-id]'),
            );

            let nextTargetEntryId: string | null = null;
            let bestDistance = Number.POSITIVE_INFINITY;

            rowElements.forEach((rowElement) => {
              const rowEntryId = rowElement.dataset.flowDashboardEntryId;
              if (!rowEntryId || rowEntryId === sourceEntryId) {
                return;
              }

              const rowRect = rowElement.getBoundingClientRect();
              const rowCenterY = rowRect.top + rowRect.height / 2;
              const distance = Math.abs(rowCenterY - sourceCenterY);

              if (distance < bestDistance) {
                bestDistance = distance;
                nextTargetEntryId = rowEntryId;
              }
            });

            setDragOverEntryId(nextTargetEntryId);
            dragOverEntryIdRef.current = nextTargetEntryId;
          },
          end(event) {
            const target = event.target as HTMLElement;
            const sourceEntryId = target.dataset.flowDashboardEntryId;
            const dragY = Number.parseFloat(target.dataset.dragY || '0') || 0;
            const targetEntryId = dragOverEntryIdRef.current;

            target.style.transform = '';
            target.style.willChange = '';
            target.style.zIndex = '';
            delete target.dataset.dragY;

            setDraggingEntryId(null);
            setDragOverEntryId(null);
            setDragPreviewShift(0);
            dragOverEntryIdRef.current = null;

            if (!sourceEntryId || Math.abs(dragY) < 12) {
              return;
            }

            if (!targetEntryId || targetEntryId === sourceEntryId) {
              return;
            }

            onReorderEntries(widget.id, sourceEntryId, targetEntryId);
          },
        },
      }),
    );

    return () => {
      setDraggingEntryId(null);
      setDragOverEntryId(null);
      setDragPreviewShift(0);
      dragOverEntryIdRef.current = null;
      interactables.forEach((interactable) => interactable.unset());
    };
  }, [entries, onReorderEntries, showSettings, widget.id]);

  const draggingIndex =
    draggingEntryId == null
      ? -1
      : entries.findIndex((entry) => entry.id === draggingEntryId);
  const targetIndex =
    dragOverEntryId == null
      ? -1
      : entries.findIndex((entry) => entry.id === dragOverEntryId);

  const getEntryPreviewTransform = (entryId: string): string | undefined => {
    if (draggingIndex < 0 || targetIndex < 0 || draggingIndex === targetIndex) {
      return undefined;
    }

    const entryIndex = entries.findIndex((entry) => entry.id === entryId);
    if (entryIndex < 0 || entryId === draggingEntryId) {
      return undefined;
    }

    const shiftPx = dragPreviewShift;
    if (shiftPx <= 0) {
      return undefined;
    }

    if (draggingIndex < targetIndex) {
      if (entryIndex > draggingIndex && entryIndex <= targetIndex) {
        return `translateY(-${shiftPx}px)`;
      }
      return undefined;
    }

    if (entryIndex >= targetIndex && entryIndex < draggingIndex) {
      return `translateY(${shiftPx}px)`;
    }

    return undefined;
  };

  const commitRename = () => {
    const normalized = draftTitle.trim();
    const current = (widget.title || '').trim();
    if (normalized === current) {
      return;
    }
    onRenameWatchlist(widget.id, normalized);
  };

  return (
    <div className="card shadow-sm h-100">
      <div className="card-body d-flex flex-column gap-3">
        <div className="d-flex justify-content-between align-items-center gap-3">
          <h5 className="mb-0">{widget.title || 'Untitled Watchlist'}</h5>
          <div className="d-flex align-items-center gap-2">
            {isEditMode && (
              <>
                <span
                  title="Drag card to reorder"
                  className="text-muted px-2 py-1 border rounded d-flex align-items-center"
                  style={{ cursor: 'grab', userSelect: 'none' }}
                >
                  <Move size={16} aria-hidden />
                </span>
                <Button
                  color="danger"
                  size="sm"
                  outline
                  onClick={() => onDeleteWatchlist(widget.id)}
                >
                  Delete Widget
                </Button>
              </>
            )}
            <Button
              color={showSettings ? 'primary' : 'secondary'}
              size="sm"
              outline={!showSettings}
              onClick={() => setShowSettings((open) => !open)}
              title={showSettings ? 'Hide settings' : 'Show settings'}
              className="d-flex align-items-center gap-1"
            >
              <SettingsIcon size={14} />
              {showSettings ? 'Hide Settings' : 'Settings'}
            </Button>
          </div>
        </div>

        {showSettings && (
          <>
            <div className="d-flex align-items-start gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Input
                  value={draftTitle}
                  onChange={(event) => setDraftTitle(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      commitRename();
                    }
                  }}
                  className="fw-semibold"
                  style={{ maxWidth: 420 }}
                />
                <Button color="secondary" size="sm" outline onClick={commitRename}>
                  Save Name
                </Button>
              </div>
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0" htmlFor={`label-mode-${widget.id}`}>
                  Component label
                </label>
                <Input
                  id={`label-mode-${widget.id}`}
                  type="select"
                  value={displaySettings.labelMode}
                  onChange={(event) =>
                    onUpdateDisplaySettings(widget.id, {
                      ...displaySettings,
                      labelMode:
                        event.target.value as FlowDashboardDisplaySettings['labelMode'],
                    })
                  }
                >
                  <option value="label">Full label</option>
                  <option value="short_label">Short label</option>
                </Input>
              </div>
              <div className="form-check mb-0">
                <Input
                  id={`show-tsp-name-${widget.id}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={displaySettings.showTspName}
                  onChange={(event) =>
                    onUpdateDisplaySettings(widget.id, {
                      ...displaySettings,
                      showTspName: event.target.checked,
                    })
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`show-tsp-name-${widget.id}`}
                >
                  Show TSP
                </label>
              </div>
            </div>

            <div className="border-top pt-3 d-flex flex-column gap-2">
              <div className="form-check mb-0">
                <Input
                  id={`override-formatting-${widget.id}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={hasFormattingOverride}
                  onChange={(event) =>
                    onUpdateFormattingOverrides(
                      widget.id,
                      event.target.checked ? formattingSettings : null,
                    )
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`override-formatting-${widget.id}`}
                >
                  Override dashboard formatting
                </label>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="form-check mb-0">
                  <Input
                    id={`compact-volumes-${widget.id}`}
                    className="form-check-input"
                    type="checkbox"
                    checked={formattingSettings.useCompactVolumes}
                    disabled={!hasFormattingOverride}
                    onChange={(event) =>
                      onUpdateFormattingOverrides(widget.id, {
                        ...formattingOverrides,
                        useCompactVolumes: event.target.checked,
                      })
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`compact-volumes-${widget.id}`}
                  >
                    Compact volumes
                  </label>
                </div>
                <label className="mb-0" htmlFor={`compact-cutoff-${widget.id}`}>
                  At or above
                </label>
                <NumericDraftInput
                  ariaLabel="Compact volume cutoff"
                  value={formattingSettings.compactVolumeCutoff}
                  disabled={!hasFormattingOverride}
                  min={1000}
                  onCommit={(compactVolumeCutoff) =>
                    onUpdateFormattingOverrides(widget.id, {
                      ...formattingOverrides,
                      compactVolumeCutoff,
                    })
                  }
                />
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="form-check mb-0">
                  <Input
                    id={`utilization-gradient-${widget.id}`}
                    className="form-check-input"
                    type="checkbox"
                    checked={formattingSettings.showUtilizationGradient}
                    disabled={!hasFormattingOverride}
                    onChange={(event) =>
                      onUpdateFormattingOverrides(widget.id, {
                        ...formattingOverrides,
                        showUtilizationGradient: event.target.checked,
                      })
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`utilization-gradient-${widget.id}`}
                  >
                    Utilization gradient
                  </label>
                </div>
                <div className="form-check mb-0">
                  <Input
                    id={`low-flow-alert-${widget.id}`}
                    className="form-check-input"
                    type="checkbox"
                    checked={formattingSettings.showLowFlowAlert}
                    disabled={!hasFormattingOverride}
                    onChange={(event) =>
                      onUpdateFormattingOverrides(widget.id, {
                        ...formattingOverrides,
                        showLowFlowAlert: event.target.checked,
                      })
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`low-flow-alert-${widget.id}`}
                  >
                    Low-flow alert
                  </label>
                </div>
                {formattingSettings.showLowFlowAlert && (
                  <>
                    <label
                      className="mb-0"
                      htmlFor={`low-flow-threshold-${widget.id}`}
                    >
                      Below
                    </label>
                    <NumericDraftInput
                      ariaLabel="Low-flow threshold"
                      value={formattingSettings.lowFlowThreshold}
                      disabled={!hasFormattingOverride}
                      min={0}
                      onCommit={(lowFlowThreshold) =>
                        onUpdateFormattingOverrides(widget.id, {
                          ...formattingOverrides,
                          lowFlowThreshold,
                        })
                      }
                    />
                    <Input
                      aria-label="Low-flow alert color"
                      type="color"
                      value={formattingSettings.lowFlowAlertColor}
                      disabled={!hasFormattingOverride}
                      onChange={(event) =>
                        onUpdateFormattingOverrides(widget.id, {
                          ...formattingOverrides,
                          lowFlowAlertColor: event.target.value,
                        })
                      }
                      style={{ width: 42, padding: 2 }}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="border-top pt-3 d-flex flex-column gap-2">
              <div className="form-check mb-0">
                <Input
                  id={`override-history-view-${widget.id}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={hasHistoryViewOverride}
                  onChange={(event) =>
                    onUpdateHistoryViewOverrides(
                      widget.id,
                      event.target.checked ? historyViewSettings : null,
                    )
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`override-history-view-${widget.id}`}
                >
                  Override dashboard history view
                </label>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <label className="mb-0" htmlFor={`history-scope-${widget.id}`}>
                  Start view
                </label>
                <Input
                  id={`history-scope-${widget.id}`}
                  type="select"
                  value={historyViewSettings.defaultScope}
                  disabled={!hasHistoryViewOverride}
                  onChange={(event) =>
                    onUpdateHistoryViewOverrides(widget.id, {
                      ...historyViewOverrides,
                      defaultScope: event.target
                        .value as FlowDashboardHistoryViewSettings['defaultScope'],
                    })
                  }
                >
                  <option value="focused">Focused date</option>
                  <option value="history">History</option>
                </Input>
                <label className="mb-0" htmlFor={`history-cycles-${widget.id}`}>
                  Cycles
                </label>
                <Input
                  id={`history-cycles-${widget.id}`}
                  type="select"
                  value={historyViewSettings.focusedCycles}
                  disabled={!hasHistoryViewOverride}
                  onChange={(event) =>
                    onUpdateHistoryViewOverrides(widget.id, {
                      ...historyViewOverrides,
                      focusedCycles: event.target
                        .value as FlowDashboardHistoryViewSettings['focusedCycles'],
                    })
                  }
                >
                  <option value="all_available">All available</option>
                  <option value="pinned">Pinned cycle</option>
                </Input>
                <label className="mb-0" htmlFor={`history-columns-${widget.id}`}>
                  Columns
                </label>
                <Input
                  id={`history-columns-${widget.id}`}
                  type="select"
                  value={historyViewSettings.cycleColumnMode}
                  disabled={!hasHistoryViewOverride}
                  onChange={(event) =>
                    onUpdateHistoryViewOverrides(widget.id, {
                      ...historyViewOverrides,
                      cycleColumnMode: event.target
                        .value as FlowDashboardHistoryViewSettings['cycleColumnMode'],
                    })
                  }
                >
                  <option value="all_cycles">All cycles</option>
                  <option value="flow_record">Flow records only</option>
                  <option value="any_record">Flow or notice records</option>
                </Input>
                {historyViewSettings.focusedCycles === 'pinned' && (
                  <Input
                    aria-label="Pinned history cycle"
                    type="select"
                    value={historyViewSettings.pinnedCycle}
                    disabled={!hasHistoryViewOverride}
                    onChange={(event) =>
                      onUpdateHistoryViewOverrides(widget.id, {
                        ...historyViewOverrides,
                        pinnedCycle: event.target
                          .value as FlowDashboardHistoryViewSettings['pinnedCycle'],
                      })
                    }
                  >
                    <option value="T">T</option>
                    <option value="E">E</option>
                    <option value="I1">I1</option>
                    <option value="I2">I2</option>
                    <option value="I3">I3</option>
                  </Input>
                )}
              </div>
            </div>

            <div>
              <div className="d-flex flex-column gap-2" ref={entriesContainerRef}>
                {entries.length === 0 ? (
                  <div className="border rounded p-3 text-muted bg-light">
                    Add capacity components to this watchlist to resolve inline flows.
                  </div>
                ) : (
                  <>
                    {entries.map((entry) => {
                      const liveMetadata = componentMetadataById.get(entry.componentId);
                      const cachedMetadata = resolvedMetadataById[entry.componentId];
                      const displayMetadata = liveMetadata || cachedMetadata;
                      const displayLabel =
                        (displaySettings.labelMode === 'short_label'
                          ? displayMetadata?.shortLabel
                          : displayMetadata?.label) || `Component #${entry.componentId}`;
                      const displayTspName =
                        displayMetadata?.tspName || 'Unknown TSP';

                      return (
                    <div
                      key={entry.id}
                      className="border rounded p-2 d-flex justify-content-between align-items-center gap-3"
                      data-flow-dashboard-entry-id={entry.id}
                      style={{
                        transform: getEntryPreviewTransform(entry.id),
                        transition:
                          draggingEntryId != null
                            ? 'transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease'
                            : 'box-shadow 140ms ease, background-color 140ms ease',
                        backgroundColor:
                          dragOverEntryId === entry.id
                            ? 'rgba(13, 110, 253, 0.08)'
                            : undefined,
                        boxShadow:
                          draggingEntryId === entry.id
                            ? '0 0 0 2px rgba(13, 110, 253, 0.3)'
                            : undefined,
                        cursor: 'grab',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                      }}
                    >
                      <div>
                        <div className="fw-semibold">{displayLabel}</div>
                        <div className="text-muted small">
                          {displaySettings.showTspName && `${displayTspName} · `}
                          {entry.capacityType} · #{entry.componentId}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                        <div className="btn-group btn-group-sm" role="group">
                          {getDirectionOptions(entry.capacityType).map((direction) => (
                            <button
                              key={direction}
                              type="button"
                              className={`btn ${
                                entry.direction === direction
                                  ? 'btn-primary'
                                  : 'btn-outline-primary'
                              }`}
                              onClick={() =>
                                onUpdateEntryDirection(widget.id, entry.id, direction)
                              }
                            >
                              {direction}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          title="Remove component"
                          aria-label="Remove component"
                          className="p-0 border-0 bg-transparent text-danger d-inline-flex align-items-center justify-content-center"
                          onClick={() => {
                            const confirmed = window.confirm(
                              `Remove ${displayLabel} from this watchlist?`,
                            );
                            if (!confirmed) {
                              return;
                            }
                            onRemoveEntry(widget.id, entry.id);
                          }}
                        >
                          <Trash2 size={16} aria-hidden />
                        </button>
                      </div>
                    </div>
                      );
                    })}
                  </>
                )}

                <button
                  type="button"
                  className="border rounded border-2 border-dashed bg-white p-3 d-flex align-items-center gap-3 text-start"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onAddComponent(widget.id)}
                >
                  <span
                    className="rounded-circle border d-flex align-items-center justify-content-center"
                    style={{ width: 34, height: 34, fontSize: 20, lineHeight: 1 }}
                  >
                    +
                  </span>
                    <span className="fw-semibold d-block">Add Component</span>
                </button>
              </div>
            </div>
          </>
        )}

        {entries.length > 0 && isLoading && !flowHistoryData && (
          <div className="d-flex justify-content-center align-items-center py-4">
            <LoadingIndicator />
          </div>
        )}

        {entries.length > 0 && hasError && (
          <div className="alert alert-danger mb-0">{errorText}</div>
        )}

        {entries.length > 0 && flowHistoryData && !hasError && (
          <>
            {isHistoryMode && (
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <div className="btn-group btn-group-sm" role="group" aria-label="History range">
                  {[7, 30, 90].map((limit) => (
                    <button
                      key={limit}
                      type="button"
                      className={`btn ${
                        flowHistoryLimit === limit && !flowHistoryStartGasDate
                          ? 'btn-primary'
                          : 'btn-outline-primary'
                      }`}
                      onClick={() => {
                        setFlowHistoryLimit(limit);
                        setFlowHistoryStartGasDate('');
                        setIsFlowHistoryDateRangeOpen(false);
                        setFlowHistoryData(null);
                        void loadPathFlows(flowHistoryEndGasDate, limit);
                      }}
                    >
                      {limit} days
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  aria-expanded={isFlowHistoryDateRangeOpen}
                  onClick={() => {
                    if (!isFlowHistoryDateRangeOpen) {
                      setFlowHistoryStartGasDate(
                        getHistoryRangeStart(
                          flowHistoryEndGasDate,
                          flowHistoryLimit,
                        ),
                      );
                    }
                    setIsFlowHistoryDateRangeOpen((isOpen) => !isOpen);
                  }}
                >
                  Date range
                </button>
                {isFlowHistoryDateRangeOpen && (
                  <div className="d-flex align-items-end flex-wrap gap-2 w-100">
                    <label className="d-flex flex-column gap-1 small mb-0">
                      <span>Start date</span>
                      <Input
                        type="date"
                        bsSize="sm"
                        value={flowHistoryStartGasDate}
                        max={flowHistoryEndGasDate}
                        onChange={(event) =>
                          setFlowHistoryStartGasDate(event.target.value)
                        }
                      />
                    </label>
                    <label className="d-flex flex-column gap-1 small mb-0">
                      <span>End date</span>
                      <Input
                        type="date"
                        bsSize="sm"
                        value={flowHistoryEndGasDate}
                        min={flowHistoryStartGasDate || undefined}
                        onChange={(event) =>
                          setFlowHistoryEndGasDate(event.target.value)
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={
                        getHistoryRangeLimit(
                          flowHistoryStartGasDate,
                          flowHistoryEndGasDate,
                        ) === null
                      }
                      onClick={() => {
                        const limit = getHistoryRangeLimit(
                          flowHistoryStartGasDate,
                          flowHistoryEndGasDate,
                        );
                        if (limit === null) return;

                        setFlowHistoryLimit(limit);
                        setFlowHistoryData(null);
                        void loadPathFlows(flowHistoryEndGasDate, limit);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>
            )}
            <CapacityComponentBulkHistory
              componentIds={null}
              data={flowHistoryData}
              limit={flowHistoryLimit}
              endGasDate={flowHistoryEndGasDate}
              startGasDate={flowHistoryStartGasDate || undefined}
              hasMoreHistory={isHistoryMode && !flowHistoryStartGasDate}
              isLoadingMoreHistory={isHistoryMode && isLoading}
              onLoadMoreHistory={loadOlderHistory}
              onHistoryScopeChange={setHistoryScope}
              conceptStorageKey={getFlowDashboardConceptStorageKey(widget.id)}
              componentLabelMode={displaySettings.labelMode}
              showTspName={displaySettings.showTspName}
              flowDashboardFormatting={formattingSettings}
              historyViewSettings={historyViewSettings}
              cycleColumnMode={historyViewSettings.cycleColumnMode}
            />
          </>
        )}
      </div>
    </div>
  );
};

export const FlowDashboard: React.FC = () => {
  const { dashboardId = '1' } = useParams<{ dashboardId: string }>();
  const { data: dashboard, isLoading, error } = useGetDashboardQuery(dashboardId);
  const [createWidget, { isLoading: isCreatingWidget }] =
    useCreateDashboardWidgetMutation();
  const [updateDashboard, { isLoading: isUpdatingDashboard }] =
    useUpdateDashboardMutation();
  const [updateWidget] = useUpdateDashboardWidgetMutation();
  const [deleteWidget] = useDeleteDashboardWidgetMutation();
  const [reorderWidgets] = useReorderDashboardWidgetsMutation();

  const widgets = useMemo(
    () => [...(dashboard?.widgets || [])].sort((a, b) => a.order_index - b.order_index),
    [dashboard?.widgets],
  );

  const entriesByWidgetId = useMemo(
    () => Object.fromEntries(widgets.map((w) => [w.id, parseWidgetEntries(w)])),
    [widgets],
  );
  const [newWidgetTitle, setNewWidgetTitle] = useState('');
  const [dashboardNameDraft, setDashboardNameDraft] = useState('');
  const [searchWidgetId, setSearchWidgetId] = useState<number | null>(null);
  const [draggingWidgetId, setDraggingWidgetId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [columnCount, setColumnCount] = useState<1 | 2 | 3>(3);
  const dashboardFormatting = getFlowDashboardFormattingSettings(dashboard?.layout);
  const dashboardHistoryView = getFlowDashboardHistoryViewSettings(
    dashboard?.layout,
  );

  const widgetColClass =
    columnCount === 1
      ? 'col-12'
      : columnCount === 2
        ? 'col-12 col-xl-6'
        : 'col-12 col-lg-6 col-xxl-4';

  useEffect(() => {
    setDashboardNameDraft(dashboard?.name || '');
  }, [dashboard?.name]);

  useEffect(() => {
    const persistedCount = dashboard?.layout?.column_count;
    if (persistedCount === 1 || persistedCount === 2 || persistedCount === 3) {
      setColumnCount(persistedCount);
      return;
    }
    setColumnCount(3);
  }, [dashboard?.id, dashboard?.layout]);

  const updateColumnCount = async (nextCount: 1 | 2 | 3) => {
    setColumnCount(nextCount);
    try {
      await updateDashboard({
        dashboardId,
        layout: {
          ...(dashboard?.layout || {}),
          column_count: nextCount,
        },
      }).unwrap();
    } catch {
      toast.error('Unable to save dashboard column layout.');
    }
  };

  const updateDashboardFormatting = async (
    settings: FlowDashboardFormattingSettings,
  ) => {
    try {
      await updateDashboard({
        dashboardId,
        layout: {
          ...(dashboard?.layout || {}),
          [FLOW_DASHBOARD_FORMATTING_KEY]: toFlowDashboardFormattingConfig(
            settings,
          ),
        },
      }).unwrap();
    } catch {
      toast.error('Unable to save dashboard formatting.');
    }
  };

  const updateDashboardHistoryView = async (
    settings: FlowDashboardHistoryViewSettings,
  ) => {
    try {
      await updateDashboard({
        dashboardId,
        layout: {
          ...(dashboard?.layout || {}),
          [FLOW_DASHBOARD_HISTORY_VIEW_KEY]: toFlowDashboardHistoryViewConfig(
            settings,
          ),
        },
      }).unwrap();
    } catch {
      toast.error('Unable to save dashboard history view.');
    }
  };

  const updateWidgetEntries = async (
    widget: DashboardWidget,
    updater: (entries: FlowDashboardEntry[]) => FlowDashboardEntry[],
  ) => {
    const nextEntries = updater(parseWidgetEntries(widget));
    await updateWidget({
      dashboardId,
      widgetId: widget.id,
      config: {
        ...(widget.config || {}),
        entries: nextEntries,
      },
    }).unwrap();
  };

  const updateWidgetDisplaySettings = async (
    widgetId: number,
    settings: FlowDashboardDisplaySettings,
  ) => {
    const widget = widgets.find((item) => item.id === widgetId);
    if (!widget) {
      return;
    }

    try {
      await updateWidget({
        dashboardId,
        widgetId,
        config: {
          ...(widget.config || {}),
          label_mode: settings.labelMode,
          show_tsp_name: settings.showTspName,
        },
      }).unwrap();
    } catch {
      toast.error('Unable to save watchlist display settings.');
    }
  };

  const updateWidgetFormattingOverrides = async (
    widgetId: number,
    overrides: FlowDashboardFormattingOverrides | null,
  ) => {
    const widget = widgets.find((item) => item.id === widgetId);
    if (!widget) {
      return;
    }

    const config = { ...(widget.config || {}) };
    if (overrides === null) {
      delete config[FLOW_DASHBOARD_FORMATTING_KEY];
    } else {
      config[FLOW_DASHBOARD_FORMATTING_KEY] =
        toFlowDashboardFormattingConfig(overrides);
    }

    try {
      await updateWidget({ dashboardId, widgetId, config }).unwrap();
    } catch {
      toast.error('Unable to save watchlist formatting.');
    }
  };

  const updateWidgetHistoryViewOverrides = async (
    widgetId: number,
    overrides: FlowDashboardHistoryViewOverrides | null,
  ) => {
    const widget = widgets.find((item) => item.id === widgetId);
    if (!widget) {
      return;
    }

    const config = { ...(widget.config || {}) };
    if (overrides === null) {
      delete config[FLOW_DASHBOARD_HISTORY_VIEW_KEY];
    } else {
      config[FLOW_DASHBOARD_HISTORY_VIEW_KEY] =
        toFlowDashboardHistoryViewConfig(overrides);
    }

    try {
      await updateWidget({ dashboardId, widgetId, config }).unwrap();
    } catch {
      toast.error('Unable to save watchlist history view.');
    }
  };

  const createNewWatchlist = async () => {
    try {
      await createWidget({
        dashboardId,
        widget_type: 'FLOW_WATCHLIST',
        title: newWidgetTitle || 'Flow Watchlist',
        config: { entries: [] },
      }).unwrap();
      setNewWidgetTitle('');
    } catch {
      toast.error('Unable to create watchlist widget.');
    }
  };

  const saveDashboardName = async () => {
    const normalized = dashboardNameDraft.trim();
    const current = (dashboard?.name || '').trim();
    if (!dashboard || normalized.length === 0 || normalized === current) {
      return;
    }

    try {
      await updateDashboard({ dashboardId, name: normalized }).unwrap();
      toast.success('Dashboard name updated');
    } catch {
      toast.error('Unable to update dashboard name.');
    }
  };

  const deleteWatchlist = async (widgetId: number) => {
    const widget = widgets.find((item) => item.id === widgetId);
    const widgetName = widget?.title?.trim() || `Widget #${widgetId}`;
    const confirmed = window.confirm(
      `Delete ${widgetName}? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }

    try {
      await deleteWidget({ dashboardId, widgetId }).unwrap();
    } catch {
      toast.error('Unable to delete watchlist widget.');
    }
  };

  const renameWatchlist = async (widgetId: number, title: string) => {
    try {
      await updateWidget({ dashboardId, widgetId, title }).unwrap();
    } catch {
      toast.error('Unable to rename watchlist widget.');
    }
  };

  const addComponentToWatchlist = async (component: CapacityComponent) => {
    if (!searchWidgetId) {
      return;
    }

    const widget = widgets.find((item) => item.id === searchWidgetId);
    if (!widget) {
      return;
    }

    const componentId = Number(component.id);
    const capacityType: CapacityType =
      component.capacity_type === 'SEGMENT' ? 'SEGMENT' : 'POINT';
    const uniqueEntryId = `${componentId}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    try {
      await updateWidgetEntries(widget, (entries) => {
        return [
          ...entries,
          {
            id: uniqueEntryId,
            componentId,
            capacityType,
            direction: getDirectionOptions(capacityType)[0] || 'RECEIPT',
            elementDirection: null,
          },
        ];
      });
      toast.success(`Added ${component.label} to watchlist`);
    } catch {
      toast.error('Unable to add component to watchlist.');
    } finally {
      setSearchWidgetId(null);
    }
  };

  const removeEntry = async (widgetId: number, entryId: string) => {
    const widget = widgets.find((item) => item.id === widgetId);
    if (!widget) {
      return;
    }
    try {
      await updateWidgetEntries(widget, (entries) =>
        entries.filter((entry) => entry.id !== entryId),
      );
    } catch {
      toast.error('Unable to remove component from watchlist.');
    }
  };

  const updateEntryDirection = async (
    widgetId: number,
    entryId: string,
    direction: FlowDashboardEntryDirection,
  ) => {
    const widget = widgets.find((item) => item.id === widgetId);
    if (!widget) {
      return;
    }
    try {
      await updateWidgetEntries(widget, (entries) =>
        entries.map((entry) =>
          entry.id === entryId ? { ...entry, direction } : entry,
        ),
      );
    } catch {
      toast.error('Unable to update direction.');
    }
  };

  const reorderEntries = async (
    widgetId: number,
    sourceEntryId: string,
    targetEntryId: string,
  ) => {
    const widget = widgets.find((item) => item.id === widgetId);
    if (!widget) {
      return;
    }

    try {
      await updateWidgetEntries(widget, (entries) => {
        const sourceIndex = entries.findIndex((entry) => entry.id === sourceEntryId);
        const targetIndex = entries.findIndex((entry) => entry.id === targetEntryId);

        if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
          return entries;
        }

        const nextEntries = [...entries];
        const [movedEntry] = nextEntries.splice(sourceIndex, 1);
        nextEntries.splice(targetIndex, 0, movedEntry);
        return nextEntries;
      });
    } catch {
      toast.error('Unable to reorder component.');
    }
  };

  const reorderWatchlists = async (sourceId: number, targetId: number) => {
    if (sourceId === targetId) {
      return;
    }

    const sourceIndex = widgets.findIndex((watchlist) => watchlist.id === sourceId);
    const targetIndex = widgets.findIndex((watchlist) => watchlist.id === targetId);

    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }

    const next = [...widgets];
    [next[sourceIndex], next[targetIndex]] = [
      next[targetIndex],
      next[sourceIndex],
    ];

    try {
      await reorderWidgets({
        dashboardId,
        widget_ids: next.map((widget) => widget.id),
      }).unwrap();
    } catch {
      toast.error('Unable to reorder watchlists.');
    }
  };

  if (isLoading) {
    return (
      <div className="container-fluid py-3 d-flex justify-content-center align-items-center h-100">
        <LoadingIndicator />
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="container-fluid py-3">
        <div className="alert alert-danger mb-0">
          Unable to load dashboard {dashboardId}.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 d-flex flex-column gap-4 overflow-auto h-100">
      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
        <div>
          {isEditMode ? (
            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
              <Input
                value={dashboardNameDraft}
                onChange={(event) => setDashboardNameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    saveDashboardName();
                  }
                }}
                style={{ minWidth: 320, maxWidth: 520 }}
              />
              <Button
                color="secondary"
                size="sm"
                outline
                onClick={saveDashboardName}
                disabled={isUpdatingDashboard}
              >
                Save Name
              </Button>
            </div>
          ) : (
            <h2 className="mb-1">{dashboard.name || 'Flow Dashboard'}</h2>
          )}
        </div>
        <Button
          color={isEditMode ? 'primary' : 'secondary'}
          onClick={() => setIsEditMode((current) => !current)}
        >
          {isEditMode ? 'Done Editing' : 'Edit Dashboard'}
        </Button>
      </div>

      {isEditMode && (
        <div className="border rounded p-3 bg-light d-flex flex-column gap-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="small text-muted">Layout</span>
            <div className="btn-group" role="group" aria-label="Dashboard columns">
              {[1, 2, 3].map((count) => (
                <button
                  key={count}
                  type="button"
                  className={`btn btn-sm ${
                    columnCount === count ? 'btn-primary' : 'btn-outline-primary'
                  }`}
                  onClick={() => updateColumnCount(count as 1 | 2 | 3)}
                  title={`${count} column${count === 1 ? '' : 's'}`}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>
          <div className="d-flex flex-column align-items-start gap-2">
            <span className="small text-muted">Volume display</span>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="form-check mb-0">
                <Input
                  id="dashboard-compact-volumes"
                  className="form-check-input"
                  type="checkbox"
                  checked={dashboardFormatting.useCompactVolumes}
                  onChange={(event) =>
                    updateDashboardFormatting({
                      ...dashboardFormatting,
                      useCompactVolumes: event.target.checked,
                    })
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="dashboard-compact-volumes"
                >
                  Compact volumes
                </label>
              </div>
              <label className="small text-muted mb-0">
                Cutoff
              </label>
              <NumericDraftInput
                ariaLabel="Compact volume cutoff"
                value={dashboardFormatting.compactVolumeCutoff}
                min={1000}
                onCommit={(compactVolumeCutoff) =>
                  updateDashboardFormatting({
                    ...dashboardFormatting,
                    compactVolumeCutoff,
                  })
                }
              />
            </div>
            <div className="form-check mb-0">
              <Input
                id="dashboard-utilization-gradient"
                className="form-check-input"
                type="checkbox"
                checked={dashboardFormatting.showUtilizationGradient}
                onChange={(event) =>
                  updateDashboardFormatting({
                    ...dashboardFormatting,
                    showUtilizationGradient: event.target.checked,
                  })
                }
              />
              <label
                className="form-check-label"
                htmlFor="dashboard-utilization-gradient"
              >
                Utilization gradient
              </label>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="form-check mb-0">
                <Input
                  id="dashboard-low-flow-alert"
                  className="form-check-input"
                  type="checkbox"
                  checked={dashboardFormatting.showLowFlowAlert}
                  onChange={(event) =>
                    updateDashboardFormatting({
                      ...dashboardFormatting,
                      showLowFlowAlert: event.target.checked,
                    })
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="dashboard-low-flow-alert"
                >
                  Low-flow alert
                </label>
              </div>
              {dashboardFormatting.showLowFlowAlert && (
                <>
                  <label className="small text-muted mb-0">
                    Threshold
                  </label>
                  <NumericDraftInput
                    ariaLabel="Low-flow threshold"
                    value={dashboardFormatting.lowFlowThreshold}
                    min={0}
                    onCommit={(lowFlowThreshold) =>
                      updateDashboardFormatting({
                        ...dashboardFormatting,
                        lowFlowThreshold,
                      })
                    }
                  />
                  <label
                    className="small text-muted mb-0"
                    htmlFor="dashboard-low-flow-alert-color"
                  >
                    Color
                  </label>
                  <Input
                    id="dashboard-low-flow-alert-color"
                    type="color"
                    value={dashboardFormatting.lowFlowAlertColor}
                    onChange={(event) =>
                      updateDashboardFormatting({
                        ...dashboardFormatting,
                        lowFlowAlertColor: event.target.value,
                      })
                    }
                    style={{ width: 42, padding: 2 }}
                  />
                </>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="small text-muted">History view</span>
            <label className="small text-muted mb-0" htmlFor="dashboard-history-scope">
              Default
            </label>
            <Input
              id="dashboard-history-scope"
              type="select"
              value={dashboardHistoryView.defaultScope}
              onChange={(event) =>
                updateDashboardHistoryView({
                  ...dashboardHistoryView,
                  defaultScope: event.target
                    .value as FlowDashboardHistoryViewSettings['defaultScope'],
                })
              }
            >
              <option value="focused">Focused date</option>
              <option value="history">History</option>
            </Input>
            <label className="small text-muted mb-0" htmlFor="dashboard-history-cycles">
              Cycles
            </label>
            <Input
              id="dashboard-history-cycles"
              type="select"
              value={dashboardHistoryView.focusedCycles}
              onChange={(event) =>
                updateDashboardHistoryView({
                  ...dashboardHistoryView,
                  focusedCycles: event.target
                    .value as FlowDashboardHistoryViewSettings['focusedCycles'],
                })
              }
            >
              <option value="all_available">All available</option>
              <option value="pinned">Pinned cycle</option>
            </Input>
            <label className="small text-muted mb-0" htmlFor="dashboard-history-columns">
              Columns
            </label>
            <Input
              id="dashboard-history-columns"
              type="select"
              value={dashboardHistoryView.cycleColumnMode}
              onChange={(event) =>
                updateDashboardHistoryView({
                  ...dashboardHistoryView,
                  cycleColumnMode: event.target
                    .value as FlowDashboardHistoryViewSettings['cycleColumnMode'],
                })
              }
            >
              <option value="all_cycles">All cycles</option>
              <option value="flow_record">Flow records only</option>
              <option value="any_record">Flow or notice records</option>
            </Input>
            {dashboardHistoryView.focusedCycles === 'pinned' && (
              <Input
                aria-label="Dashboard pinned history cycle"
                type="select"
                value={dashboardHistoryView.pinnedCycle}
                onChange={(event) =>
                  updateDashboardHistoryView({
                    ...dashboardHistoryView,
                    pinnedCycle: event.target
                      .value as FlowDashboardHistoryViewSettings['pinnedCycle'],
                  })
                }
              >
                <option value="T">T</option>
                <option value="E">E</option>
                <option value="I1">I1</option>
                <option value="I2">I2</option>
                <option value="I3">I3</option>
              </Input>
            )}
          </div>
        </div>
      )}

      <div className="row g-3">
        {widgets.map((widget) => (
          <div
            className={widgetColClass}
            key={widget.id}
            draggable={isEditMode}
            onDragStart={(event) => {
              if (!isEditMode) {
                event.preventDefault();
                return;
              }
              setDraggingWidgetId(widget.id);
            }}
            onDragOver={(event) => {
              if (isEditMode) {
                event.preventDefault();
              }
            }}
            onDrop={() => {
              if (!isEditMode) {
                return;
              }
              if (!draggingWidgetId) {
                return;
              }
              reorderWatchlists(draggingWidgetId, widget.id);
              setDraggingWidgetId(null);
            }}
            onDragEnd={() => setDraggingWidgetId(null)}
            style={{
              cursor: isEditMode ? 'grab' : 'default',
              userSelect: isEditMode ? 'none' : undefined,
              WebkitUserSelect: isEditMode ? 'none' : undefined,
            }}
          >
            <WatchlistPanel
              widget={widget}
              entries={entriesByWidgetId[widget.id] ?? []}
              isEditMode={isEditMode}
              onAddComponent={setSearchWidgetId}
              onDeleteWatchlist={deleteWatchlist}
              onRenameWatchlist={renameWatchlist}
              onRemoveEntry={removeEntry}
              onReorderEntries={reorderEntries}
              onUpdateEntryDirection={updateEntryDirection}
              onUpdateDisplaySettings={updateWidgetDisplaySettings}
              dashboardFormatting={dashboardFormatting}
              onUpdateFormattingOverrides={updateWidgetFormattingOverrides}
              dashboardHistoryView={dashboardHistoryView}
              onUpdateHistoryViewOverrides={updateWidgetHistoryViewOverrides}
            />
          </div>
        ))}

        {(isEditMode || widgets.length === 0) && (
          <div className={widgetColClass}>
            <div className="card shadow-sm h-100 border-2 border-dashed">
              <div className="card-body d-flex flex-column justify-content-center align-items-center text-center gap-3 py-5">
                <div
                  className="rounded-circle border d-flex align-items-center justify-content-center"
                  style={{ width: 52, height: 52, fontSize: 28, lineHeight: 1 }}
                >
                  +
                </div>
                <div>
                  <div className="fw-semibold">Add Widget</div>
                  <div className="text-muted small">
                    {isEditMode
                      ? 'Create a new watchlist widget for this dashboard.'
                      : 'Turn on Edit Dashboard to add widgets.'}
                  </div>
                </div>

                {isEditMode ? (
                  <div className="d-flex gap-2 align-items-center flex-wrap justify-content-center">
                    <Input
                      placeholder="New watchlist name"
                      value={newWidgetTitle}
                      onChange={(event) => setNewWidgetTitle(event.target.value)}
                      style={{ minWidth: 260 }}
                    />
                    <Button
                      color="primary"
                      onClick={createNewWatchlist}
                      disabled={isCreatingWidget}
                    >
                      Create Watchlist
                    </Button>
                  </div>
                ) : (
                  <Button color="secondary" outline onClick={() => setIsEditMode(true)}>
                    Edit Dashboard
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <CapacityComponentSearch
        isSearching={searchWidgetId !== null}
        onClose={() => setSearchWidgetId(null)}
        onSelect={addComponentToWatchlist}
        filters={{}}
      />
    </div>
  );
};
